import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import EventFeed from "@/components/EventFeed";
import RaidWheel, { computeRot } from "@/components/RaidWheel";
import RpgBattle from "@/components/RpgBattle";
import OverlayAvatar from "@/components/OverlayAvatar";
import { McGiftAlert } from "@/components/McGiftAlert";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

const MEDIA_POS = {
  center: "items-center justify-center",
  top: "items-start justify-center",
  bottom: "items-end justify-center",
  "top-left": "items-start justify-start",
  "top-right": "items-start justify-end",
  "bottom-left": "items-end justify-start",
  "bottom-right": "items-end justify-end",
};
const MEDIA_SIZE = {
  small: "max-w-[20vw] max-h-[28vh]",
  medium: "max-w-[38vw] max-h-[45vh]",
  large: "max-w-[60vw] max-h-[65vh]",
  full: "max-w-[85vw] max-h-[85vh]",
};

export default function StreamOverlay() {
  const [started, setStarted] = useState(true);
  const [now, setNow] = useState(null);
  const [wheel, setWheel] = useState(null); // {multiplier, rot}

  const ytRef = useRef(null);
  const cur = useRef({ plId: null, index: -1, playing: false });
  const ttsAfter = useRef(new Date().toISOString());
  const soundAfter = useRef(new Date().toISOString());
  const mediaAfter = useRef(new Date().toISOString());
  const queue = useRef([]);
  const speaking = useRef(false);
  const seen = useRef(new Set());
  const audioCfg = useRef({ duck_volume: 15, music_volume: 100, chime_enabled: true, chime_volume: 50 });
  const actx = useRef(null);
  const mediaTimer = useRef(null);
  const [media, setMedia] = useState(null); // {image_url, sound_url, duration}
  const [slots, setSlots] = useState(null);
  const [mcGift, setMcGift] = useState(null);
  const mcGiftTimer = useRef(null);
  const [fishing, setFishing] = useState(null);
  const [layout, setLayout] = useState(null);
  const [singing, setSinging] = useState(null); // {title, requested_by} while an AI cover plays
  const [stashPop, setStashPop] = useState(null); // {cookies, cupcakes} shown briefly
  const stashPrev = useRef(null);
  const stashTimer = useRef(null);
  const layoutRef = useRef(null);
  const overlayKey = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "").get("key") || "";
  const [keyOk, setKeyOk] = useState(null); // null=checking, true, false

  useEffect(() => {
    if (overlayKey) api.defaults.headers.common["X-Overlay-Key"] = overlayKey;
    api.get(`/overlay/check?k=${encodeURIComponent(overlayKey)}`)
      .then((r) => setKeyOk(!!r.data.valid))
      .catch(() => setKeyOk(false));
  }, []);

  const start = () => {
    setStarted(true);
    try {
      if (!actx.current) actx.current = new (window.AudioContext || window.webkitAudioContext)();
      actx.current.resume?.();
    } catch (e) {}
    try { ytRef.current?.playVideo?.(); } catch (e) {}
  };

  // Audio is always on: OBS autoplays; in a normal browser the first click/keypress silently unlocks it.
  useEffect(() => {
    start();
    const unlock = () => { start(); window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => { window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
  }, []);

  // audio config poll
  useEffect(() => {
    if (!started || keyOk !== true) return;
    const poll = async () => { try { audioCfg.current = (await api.get("/config/audio")).data; } catch (e) {} };
    poll();
    const t = setInterval(poll, 4000);
    return () => clearInterval(t);
  }, [started, keyOk]);

  // overlay layout poll
  useEffect(() => {
    if (keyOk !== true) return;
    const poll = async () => { try { const d = (await api.get("/overlay/layout")).data; setLayout(d); layoutRef.current = d; } catch (e) {} };
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [keyOk]);

  // stash poll — pop the jar+tray briefly when it changes
  useEffect(() => {
    if (keyOk !== true) return;
    const poll = async () => {
      try {
        const { data } = await api.get("/stash");
        const cur = { cookies: data.cookies || 0, cupcakes: data.cupcakes || 0 };
        const prev = stashPrev.current;
        if (prev && (prev.cookies !== cur.cookies || prev.cupcakes !== cur.cupcakes)) {
          setStashPop(cur);
          const su = layoutRef.current?.stash_sound_url;
          if (su) { try { const a = new Audio(su.startsWith("http") ? su : ORIGIN + su); a.volume = 0.8; a.play().catch(() => {}); } catch (e) { playChime(); } }
          else playChime();
          clearTimeout(stashTimer.current);
          stashTimer.current = setTimeout(() => setStashPop(null), 5000);
        }
        stashPrev.current = cur;
      } catch (e) {}
    };
    poll();
    const t = setInterval(poll, 3000);
    return () => { clearInterval(t); clearTimeout(stashTimer.current); };
  }, [keyOk]);

  // YouTube IFrame API
  useEffect(() => {
    if (!started || keyOk !== true) return;
    const createPlayer = () => {
      ytRef.current = new window.YT.Player("yt-player", {
        height: "100%", width: "100%",
        playerVars: { autoplay: 1, controls: 0, modestbranding: 1, rel: 0 },
        events: {
          onReady: (e) => e.target.setVolume(audioCfg.current.music_volume ?? 100),
          onStateChange: (e) => { if (e.data === 0) api.post("/player/next").catch(() => {}); },
        },
      });
    };
    if (window.YT && window.YT.Player) createPlayer();
    else {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
      window.onYouTubeIframeAPIReady = createPlayer;
    }
  }, [started, keyOk]);

  // player state poll
  useEffect(() => {
    if (!started || keyOk !== true) return;
    const poll = async () => {
      try {
        const { data: s } = await api.get("/player/state");
        const yt = ytRef.current;
        if (!yt || !yt.loadVideoById) return;
        const pl = s.playlist;
        if (!s.playlist_id || !pl || !pl.songs?.length) {
          if (cur.current.plId) { yt.stopVideo(); setNow(null); }
          cur.current = { plId: null, index: -1, playing: false };
          return;
        }
        const song = pl.songs[s.index];
        const trackChanged = s.playlist_id !== cur.current.plId || s.index !== cur.current.index;
        if (trackChanged && song) {
          yt.loadVideoById(song.video_id);
          yt.setVolume(audioCfg.current.music_volume ?? 100);
          setNow({ title: song.title, thumb: song.thumbnail, playing: s.is_playing });
          if (!s.is_playing) setTimeout(() => yt.pauseVideo(), 400);
        } else if (!trackChanged) {
          if (s.is_playing && !cur.current.playing) yt.playVideo();
          if (!s.is_playing && cur.current.playing) yt.pauseVideo();
          setNow((n) => (n ? { ...n, playing: s.is_playing } : n));
        }
        cur.current = { plId: s.playlist_id, index: s.index, playing: s.is_playing };
      } catch (e) {}
    };
    const t = setInterval(poll, 1500);
    poll();
    return () => clearInterval(t);
  }, [started, keyOk]);

  // TTS + sound queue poll
  useEffect(() => {
    if (!started || keyOk !== true) return;
    const poll = async () => {
      try {
        const [tRes, sRes] = await Promise.all([
          api.get(`/tts/queue?after=${encodeURIComponent(ttsAfter.current)}`),
          api.get(`/sounds/queue?after=${encodeURIComponent(soundAfter.current)}`),
        ]);
        if (tRes.data.length) {
          ttsAfter.current = tRes.data[tRes.data.length - 1].created_at;
          tRes.data.forEach((it) => { if (!seen.current.has(it.id)) { seen.current.add(it.id); queue.current.push({ kind: "tts", text: it.text }); } });
        }
        if (sRes.data.length) {
          soundAfter.current = sRes.data[sRes.data.length - 1].created_at;
          sRes.data.forEach((it) => { if (!seen.current.has(it.id)) { seen.current.add(it.id); queue.current.push({ kind: "sound", url: it.url, sing: it.kind === "sing" ? { title: it.title, requested_by: it.requested_by } : null }); } });
        }
        pump();
      } catch (e) {}
    };
    const t = setInterval(poll, 1500);
    poll();
    return () => clearInterval(t);
  }, [started, keyOk]);

  const duck = (v) => { try { ytRef.current?.setVolume?.(v); } catch (e) {} };

  // Media redeem popup poll (picture + sound) — queued so they don't overlap
  const mediaQueue = useRef([]);
  const mediaPlaying = useRef(false);
  useEffect(() => {
    if (!started || keyOk !== true) return;
    const pumpMedia = () => {
      if (mediaPlaying.current || mediaQueue.current.length === 0) return;
      mediaPlaying.current = true;
      const ev = mediaQueue.current.shift();
      const url = ev.image_url ? (ev.image_url.startsWith("http") ? ev.image_url : ORIGIN + ev.image_url) : "";
      setMedia({ image_url: url, name: ev.name, position: ev.position || "center", size: ev.size || "large" });
      duck(audioCfg.current.duck_volume ?? 15);
      if (ev.sound_url) {
        try {
          const s = new Audio(ev.sound_url.startsWith("http") ? ev.sound_url : ORIGIN + ev.sound_url);
          s.play().catch(() => {});
        } catch (e) {}
      }
      clearTimeout(mediaTimer.current);
      mediaTimer.current = setTimeout(() => {
        setMedia(null);
        duck(audioCfg.current.music_volume ?? 100);
        mediaPlaying.current = false;
        setTimeout(pumpMedia, 350);
      }, Math.max(1, ev.duration || 8) * 1000);
    };
    const poll = async () => {
      try {
        const r = await api.get(`/media/queue?after=${encodeURIComponent(mediaAfter.current)}`);
        if (r.data.length) {
          mediaAfter.current = r.data[r.data.length - 1].created_at;
          r.data.forEach((ev) => { if (!seen.current.has(ev.id)) { seen.current.add(ev.id); mediaQueue.current.push(ev); } });
          pumpMedia();
        }
      } catch (e) {}
    };
    const t = setInterval(poll, 1500);
    poll();
    return () => { clearInterval(t); clearTimeout(mediaTimer.current); };
  }, [started, keyOk]);

  const playChime = () => {
    const cfg = audioCfg.current;
    if (!cfg.chime_enabled) return;
    try {
      if (!actx.current) actx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = actx.current;
      const t0 = ctx.currentTime;
      const vol = (cfg.chime_volume ?? 50) / 100 * 0.4;
      [988, 1319].forEach((f, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = f;
        const s = t0 + i * 0.11;
        g.gain.setValueAtTime(0, s);
        g.gain.linearRampToValueAtTime(vol, s + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, s + 0.4);
        o.connect(g); g.connect(ctx.destination);
        o.start(s); o.stop(s + 0.45);
      });
    } catch (e) {}
  };

  const WHEEL = [1.5, 2, 2.5, 3, 5];
  const slotsTimer = useRef(null);
  const fishTimer = useRef(null);

  const playEventSound = (kind) => {
    const map = { fish: "fish", slots: "slots", gamble: "slots", gift: "gift", hug: "gift", payout: "gift", raid: "raid", wheel: "raid", mc_gift: "mc_gift" };
    const url = audioCfg.current?.event_sounds?.[map[kind]];
    if (url) {
      try { new Audio(url.startsWith("http") ? url : ORIGIN + url).play().catch(() => {}); return true; } catch (e) {}
    }
    return false;
  };

  const showFishing = (e) => {
    const size = e.size || (e.text || "").match(/(\d+)cm/)?.[1];
    const junk = !!e.junk;
    const abs = (u) => (u ? (u.startsWith("http") ? u : ORIGIN + u) : "");
    setFishing({ phase: "cast", user: e.username || "someone", cast_gif: abs(e.cast_gif), rod_emoji: e.rod_emoji || "🎣" });
    setTimeout(() => setFishing((f) => (f ? {
      ...f, phase: "result", size, junk,
      emoji: e.fish_emoji || "🐟", fish_image: abs(e.fish_image), catch_gif: abs(e.catch_gif),
      rare: !!e.rare, fish_name: e.fish_name || "",
    } : f)), 1300);
    clearTimeout(fishTimer.current);
    fishTimer.current = setTimeout(() => setFishing(null), 6000);
  };

  const onNewEvents = (items = []) => {
    let custom = false;
    items.forEach((e) => {
      if (playEventSound(e.kind)) custom = true;
      if (e.kind === "slots") {
        setSlots(e);
        clearTimeout(slotsTimer.current);
        slotsTimer.current = setTimeout(() => setSlots(null), 7000);
      }
      if (e.kind === "fish") showFishing(e);
      if (e.kind === "mc_gift") {
        setMcGift(e);
        clearTimeout(mcGiftTimer.current);
        mcGiftTimer.current = setTimeout(() => setMcGift(null), 10000);
      }
    });
    if (!custom) playChime();
  };
  const triggerWheel = (m) => {
    setWheel({ multiplier: m, rot: 0 });
    setTimeout(() => setWheel({ multiplier: m, rot: computeRot(m) }), 60);
    setTimeout(() => setWheel(null), 8000);
  };

  const pump = async () => {
    if (speaking.current || queue.current.length === 0) return;
    speaking.current = true;
    const item = queue.current.shift();
    duck(audioCfg.current.duck_volume ?? 15);
    try {
      if (item.kind === "sound") {
        const audio = new Audio(item.url.startsWith("http") ? item.url : ORIGIN + item.url);
        audio.preload = "auto";
        let finished = false;
        const done = () => { if (finished) return; finished = true; clearTimeout(guard); setSinging(null); finishSpeak(); };
        // never let a stalled download block the TTS/sound queue forever
        let guard = setTimeout(() => { if (audio.paused || audio.readyState < 3) { console.warn("overlay sound: giving up loading", item.url); done(); } }, item.sing ? 45000 : 15000);
        audio.onplaying = () => { clearTimeout(guard); if (item.sing) setSinging(item.sing); guard = setTimeout(done, ((audio.duration || 600) + 15) * 1000); };
        audio.onended = done; audio.onerror = (e) => { console.warn("overlay sound error", audio.error?.code, item.url); done(); };
        await audio.play();
        return;
      }
      const res = await fetch(`${API}/tts/speak`, {
        method: "POST", headers: { "Content-Type": "application/json", "X-Overlay-Key": overlayKey },
        body: JSON.stringify({ text: item.text }),
      });
      if (res.status === 204 || !res.ok) { await new Promise((r) => setTimeout(r, 2500)); finishSpeak(); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      try { if (!actx.current) actx.current = new (window.AudioContext || window.webkitAudioContext)(); const src = actx.current.createMediaElementSource(audio); const an = actx.current.createAnalyser(); an.fftSize = 512; src.connect(an); an.connect(actx.current.destination); window.__pcAnalyser = an; audio.addEventListener("ended", () => { if (window.__pcAnalyser === an) window.__pcAnalyser = null; }); } catch (e) {}
      audio.onended = () => { URL.revokeObjectURL(url); finishSpeak(); };
      audio.onerror = () => { URL.revokeObjectURL(url); finishSpeak(); };
      await audio.play();
    } catch (e) { finishSpeak(); }
  };

  const finishSpeak = () => {
    duck(audioCfg.current.music_volume ?? 100);
    speaking.current = false;
    setTimeout(pump, 250);
  };

  const L = layout || {};
  const posStyle = (key) => {
    const e = L[key];
    if (!e) return {};
    return { position: "absolute", left: `${e.x}%`, top: `${e.y}%`, transform: `scale(${e.scale ?? 1})`, transformOrigin: "top left" };
  };
  const vis = (key) => !L[key] || L[key].visible !== false;
  const abs = (u) => (u ? (u.startsWith("http") ? u : ORIGIN + u) : "");

  const StashChip = ({ emoji, img, label, value, color }) => (
    <div className="flex items-center gap-2 rounded-xl px-3 py-2" style={{ background: "rgba(10,10,10,0.82)", border: `1px solid ${color}88`, boxShadow: `0 0 18px ${color}55` }}>
      {img ? <img src={abs(img)} alt="" className="w-9 h-9 object-contain" /> : <span style={{ fontSize: 26 }}>{emoji}</span>}
      <div className="leading-tight">
        <div className="text-[9px] tracking-widest font-800" style={{ color, fontFamily: "Outfit, sans-serif" }}>{label}</div>
        <div className="text-white text-lg font-800" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
      </div>
    </div>
  );

  return (
    <div style={{ background: "transparent" }} className="min-h-screen w-full relative overflow-hidden">
      {keyOk === true && vis("avatar") && <div style={{ ...posStyle("avatar"), zIndex: 22 }} data-testid="overlay-avatar"><OverlayAvatar /></div>}
      {keyOk === false && (
        <div className="absolute inset-0 flex items-center justify-center" data-testid="overlay-locked">
          <div className="text-center px-6 py-5 rounded-2xl bg-[#0a0a0a]/90 border border-white/10">
            <div className="text-primary text-sm font-heading font-800 tracking-widest mb-1">OVERLAY LOCKED</div>
            <p className="text-xs text-muted-foreground max-w-xs">Open this overlay using the private link from your dashboard (OBS Overlay tab).</p>
          </div>
        </div>
      )}

      <div style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}>
        <div id="yt-player" />
      </div>

      {singing && (
        <div className="animate-fade-up" data-testid="overlay-now-singing"
          style={{ position: "absolute", left: "50%", bottom: "6%", transform: "translateX(-50%)", zIndex: 40, ...(L.now_singing ? { left: `${L.now_singing.x}%`, top: `${L.now_singing.y}%`, bottom: "auto", transform: `scale(${L.now_singing.scale ?? 1})`, transformOrigin: "top left" } : {}) }}>
          <div className="flex items-center gap-3 rounded-2xl px-4 py-3" style={{ background: "rgba(10,10,10,0.85)", border: "1px solid rgba(244,114,182,0.6)", boxShadow: "0 0 28px rgba(244,114,182,0.35)", backdropFilter: "blur(12px)" }}>
            <div className="flex items-end gap-[3px] h-6">
              {[0, 1, 2, 3, 4].map((n) => <span key={n} className="w-1 rounded-full bg-pink-400" style={{ height: "100%", animation: `sing-bar 0.9s ease-in-out ${n * 0.12}s infinite alternate` }} />)}
            </div>
            <div className="leading-tight">
              <div className="text-[9px] tracking-widest font-800 text-pink-400" style={{ fontFamily: "Outfit, sans-serif" }}>PANCAKECUPCAKE_ IS SINGING</div>
              <div className="text-white text-base font-800 max-w-[520px] truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{singing.title}</div>
              {singing.requested_by && <div className="text-[10px] text-white/60">requested by {singing.requested_by}</div>}
            </div>
          </div>
          <style>{`@keyframes sing-bar { from { transform: scaleY(0.25); } to { transform: scaleY(1); } }`}</style>
        </div>
      )}

      {keyOk === true && vis("event_feed") && (
        <div style={{ ...posStyle("event_feed"), zIndex: 20 }}>
          <EventFeed className="items-start" onNew={onNewEvents} onWheel={triggerWheel} exclude={["fish", "slots", "mc_gift"]} />
        </div>
      )}

      {/* Minecraft gift / redeem alert */}
      {mcGift && vis("mc_gift") && <McGiftAlert ev={mcGift} style={posStyle("mc_gift")} />}

      {/* Slots popup */}
      {slots && vis("slots") && (
        <div className="z-30 animate-fade-up rounded-xl px-4 py-3 max-w-sm backdrop-blur-md"
             style={{ ...posStyle("slots"), background: "rgba(10,10,10,0.85)", border: "1px solid #FF3366", boxShadow: "0 0 24px #FF336655" }}
             data-testid="overlay-slots">
          <div className="text-[10px] font-800 tracking-widest mb-1" style={{ color: "#FF3366", fontFamily: "Outfit, sans-serif" }}>🎰 SLOTS</div>
          <div className="text-white text-sm font-medium leading-snug">{slots.text}</div>
        </div>
      )}

      {/* Fishing animation */}
      {fishing && vis("fishing") && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center animate-fade-up pointer-events-none" data-testid="overlay-fishing">
          {fishing.phase === "result" && fishing.rare && !fishing.junk && (
            <div className="mb-2 px-4 py-1 rounded-full text-black text-sm font-800 animate-bounce"
                 style={{ background: "linear-gradient(90deg,#ffd700,#ff8a00)", boxShadow: "0 0 24px #ffd70088", fontFamily: "Outfit, sans-serif" }}
                 data-testid="overlay-fish-rare">
              🌟 RARE CATCH! 🌟
            </div>
          )}
          {fishing.phase === "cast" && fishing.cast_gif ? (
            <img src={fishing.cast_gif} alt="" className="max-w-[280px] max-h-[220px] object-contain" />
          ) : fishing.phase === "result" && fishing.catch_gif ? (
            <div className="relative flex items-center justify-center">
              <img src={fishing.catch_gif} alt="" className="max-w-[280px] max-h-[220px] object-contain" />
              {!fishing.junk && (
                fishing.fish_image
                  ? <img src={fishing.fish_image} alt="" className="absolute w-20 h-20 object-contain animate-bounce" style={{ filter: fishing.rare ? "drop-shadow(0 0 12px #ffd700)" : "none" }} />
                  : <div className="absolute text-5xl animate-bounce">{fishing.emoji || "🐟"}</div>
              )}
            </div>
          ) : (
            <>
              <div className="text-6xl" style={{ transform: fishing.phase === "cast" ? "rotate(-18deg)" : "rotate(8deg)", transition: "transform .5s" }}>{fishing.rod_emoji || "🎣"}</div>
              <div className="bg-white/70 w-0.5" style={{ height: fishing.phase === "cast" ? 70 : 26, transition: "height .6s" }} />
              {fishing.phase === "result" && !fishing.junk && (
                fishing.fish_image
                  ? <img src={fishing.fish_image} alt="" className="w-16 h-16 object-contain animate-bounce -mt-1" style={{ filter: fishing.rare ? "drop-shadow(0 0 12px #ffd700)" : "none" }} />
                  : <div className="text-4xl animate-bounce -mt-1">{fishing.emoji || "🐟"}</div>
              )}
            </>
          )}
          <div className="mt-2 px-3 py-1 rounded-full text-white text-sm" style={{ background: "rgba(10,10,10,0.85)", border: `1px solid ${fishing.rare && fishing.phase === "result" ? "#ffd700" : "#38bdf8"}` }}>
            {fishing.phase === "cast"
              ? `🎣 ${fishing.user} casts out…`
              : fishing.junk ? `${fishing.user} caught junk!` : `${fishing.user} caught a ${fishing.size}cm ${fishing.fish_name || "fish"}!`}
          </div>
        </div>
      )}

      {/* Raid reward wheel */}
      <RaidWheel wheel={wheel} />

      {/* RPG idle-battler */}
      {keyOk === true && vis("rpg") && <RpgBattle />}

      {/* Media redeem: picture at configured position + size */}
      {media && media.image_url && (
        <div
          className={`absolute inset-0 z-40 flex animate-fade-up pointer-events-none p-10 ${MEDIA_POS[media.position] || MEDIA_POS.center}`}
          data-testid="overlay-media-popup"
        >
          <img
            src={media.image_url}
            alt={media.name || ""}
            className={`object-contain rounded-2xl ${MEDIA_SIZE[media.size] || MEDIA_SIZE.large}`}
            style={{ boxShadow: "0 0 60px rgba(255,51,102,0.55)", border: "3px solid rgba(255,51,102,0.7)" }}
          />
        </div>
      )}

      {/* Pancake stash (jar + tray) — pops in above the music card when it changes */}
      {stashPop && vis("stash") && (
        <div className="flex items-center gap-2 animate-fade-up" style={{ ...posStyle("stash"), zIndex: 25 }} data-testid="overlay-stash">
          <StashChip emoji={L.stash_jar_emoji || "🫙"} img={L.stash_jar_img} label={L.stash_jar_label || "Cookie Jar"} value={stashPop.cookies} color="#f0b429" />
          <StashChip emoji={L.stash_tray_emoji || "🧁"} img={L.stash_tray_img} label={L.stash_tray_label || "Cupcake Tray"} value={stashPop.cupcakes} color="#FF3366" />
        </div>
      )}

      {now && vis("now_playing") && (
        <div className="flex items-center gap-3 rounded-2xl px-4 py-3 backdrop-blur-md animate-fade-up"
             style={{ ...posStyle("now_playing"), background: "rgba(10,10,10,0.82)", border: "1px solid rgba(255,51,102,0.5)", boxShadow: "0 0 24px rgba(255,51,102,0.35)", zIndex: 24 }}
             data-testid="overlay-now-playing">
          <img src={now.thumb} alt="" className={`w-14 h-14 rounded-full object-cover ${now.playing ? "spin-slow" : ""}`} style={{ border: "2px solid #FF3366" }} />
          <div className="max-w-[260px]">
            <div className="text-[10px] tracking-widest text-primary font-800" style={{ fontFamily: "Outfit, sans-serif" }}>{now.playing ? "♪ NOW PLAYING" : "⏸ PAUSED"}</div>
            <div className="text-white text-sm font-medium truncate">{now.title}</div>
          </div>
        </div>
      )}
    </div>
  );
}
